import { View, Text, ScrollView, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import RecentVisitCard from './RecentVisitCard';
import ContentLoader from '@/components/General/ContentLoader';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { getRecents, removeFromRecents, clearRecents } from '@/utils/recentlyVisited';
import { createShortcut } from '@/data/shortcuts';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import FeatureIcon from '@/components/General/FeatureIcon';
import CutCard from '@/components/General/CutCard';
import Unicon from '@/components/General/Unicon';
import { showAlert } from '@/utils/showAlert';
import dashboardStyles from './dashboardStyles';

const FILTER_OPTIONS = ['All', 'People', 'Events', 'Tasks', 'Roles', 'Training', 'Automations', 'Small Groups', 'Connect', 'Forms'];

// Normalize legacy stored module names (P2 rename) so old recents display
// under the new label and hit the Automations filter.
const normalizeModule = (module) => module === 'Paths' ? 'Automations' : module;

const RecentlyVisitedSection = () => {
  const queryClient = useQueryClient();
  const { data: loggedInUser } = Query.people.loggedInUser();
  const [recents, setRecents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All');

  useEffect(() => {
    loadRecents();
  }, []);

  const loadRecents = async () => {
    setIsLoading(true);
    const data = await getRecents();
    setRecents(data.map(item => ({ ...item, module: normalizeModule(item.module) })));
    setIsLoading(false);
  };

  const applyFilter = (data, filter) => {
    if (filter === 'All') {
      return data;
    }
    return data.filter(item => item.module === filter);
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const handleRemove = async (item) => {
    await removeFromRecents(item.id, item.url);
    const updated = recents.filter(r => !(r.id === item.id && r.url === item.url));
    setRecents(updated);
  };

  const handleAddToShortcuts = async (item) => {
    const moduleIcon = {
      'People': 'people-outline',
      'Events': 'calendar-outline',
      'Tasks': 'checkbox-outline',
      'Roles': 'shield-outline',
      'Training': 'school-outline',
      'Automations': 'git-network-outline',
      'Small Groups': 'book-outline',
      'Connect': 'chatbubbles-outline',
      'Forms': 'document-text-outline',
    }[item.module] || 'link-outline';

    const newShortcut = await createShortcut({
      personID: loggedInUser?.id,
      screen: item.url,
      label: item.title,
      icon: moduleIcon,
    });
    queryClient.setQueryData(
      QueryKeys.shortcuts.byPersonId(loggedInUser?.id),
      (old) => old ? [...old, newShortcut] : [newShortcut]
    );
    queryClient.setQueryData(
      QueryKeys.dashboardData.byPersonId(loggedInUser?.id),
      (old) => old ? { ...old, shortcuts: [...(old.shortcuts || []), newShortcut] } : old
    );
  };

  const handleClear = () => showAlert({
    title: 'Clear recently visited?',
    subtitle: 'Remove all recently visited items?',
    confirmText: 'Clear',
    isDestructive: true,
    onConfirm: async () => {
      await clearRecents();
      setRecents([]);
    },
  });

  const filteredRecents = applyFilter(recents, activeFilter);

  if (isLoading) {
    return (
      <CutCard testID="dashboard-section-recentlyVisited" style={[styles.card, dashboardStyles.card]} contentStyle={dashboardStyles.content}>
        <View style={[styles.header, dashboardStyles.header]}>
          <View style={[styles.titleRow, dashboardStyles.titleRow]}>
            <FeatureIcon feature="recent" />
            <Text style={[styles.title, dashboardStyles.title]}>Recently Visited</Text>
          </View>
        </View>
        <View style={styles.loadingWrap}>
          <ContentLoader />
        </View>
      </CutCard>
    );
  }

  if (recents.length === 0) {
    return (
      <CutCard testID="dashboard-section-recentlyVisited" style={[styles.card, dashboardStyles.card]} contentStyle={dashboardStyles.content}>
        <View style={[styles.header, dashboardStyles.header]}>
          <View style={[styles.titleRow, dashboardStyles.titleRow]}>
            <FeatureIcon feature="recent" />
            <Text style={[styles.title, dashboardStyles.title]}>Recently Visited</Text>
          </View>
        </View>
        <EmptyListPlaceholder size={90} type="complete" message="No recently visited items" spacing={8} />
      </CutCard>
    );
  }

  return (
    <CutCard testID="dashboard-section-recentlyVisited" style={[styles.card, dashboardStyles.card]} contentStyle={dashboardStyles.content}>
      <View style={[styles.header, dashboardStyles.header]}>
        <View style={[styles.titleRow, dashboardStyles.titleRow]}>
          <FeatureIcon feature="recent" />
          <Text style={[styles.title, dashboardStyles.title]}>Recently Visited</Text>
        </View>
        <Pressable
          testID="dashboard-recents-clear"
          style={styles.clearButton}
          onPress={handleClear}
          accessibilityRole="button"
          accessibilityLabel="Clear recently visited"
        >
          <Unicon name="trash-outline" size={16} colorVariant="textSecondary" />
        </Pressable>
      </View>

      {/* Filter Pills */}
      <View style={dashboardStyles.filterStrip}>
        <ScrollView
          horizontal
          style={dashboardStyles.filterStrip}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filterScroll}
          keyboardShouldPersistTaps="handled"
        >
          {FILTER_OPTIONS.map((filter) => (
            <Pressable
              key={filter}
              style={[
                styles.filterPill,
                activeFilter === filter && styles.filterPillActive,
              ]}
              onPress={() => handleFilterChange(filter)}
            >
              <Text
                style={[
                  styles.filterPillText,
                  activeFilter === filter && styles.filterPillTextActive,
                ]}
              >
                {filter}
              </Text>
            </Pressable>
          ))}
        </ScrollView>
      </View>

      {/* Recents List */}
      {filteredRecents.length === 0 ? (
        <EmptyListPlaceholder size={90} type="complete" message="No items in this category" spacing={8} />
      ) : (
        <ScrollView
          testID="dashboard-recents-scroll"
          style={dashboardStyles.scroll}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          nestedScrollEnabled
        >
          <View style={styles.list}>
            {filteredRecents.map((item) => (
              <RecentVisitCard
                key={`${item.id}-${item.url}`}
                item={item}
                onRemove={() => handleRemove(item)}
                onAddToShortcuts={() => handleAddToShortcuts(item)}
              />
            ))}
          </View>
        </ScrollView>
      )}
    </CutCard>
  );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, typography }) => ({
  card: {
    width: {
      xs: "100%",
      lg: "48.5%",
    },
    borderRadius: roundness.md,
    padding: spacing.md,
    paddingBottom: spacing.xs,
    backgroundColor: colors.surface,
    overflow: "visible",
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs + 2,
  },
  iconWrap: {
    width: 24,
    height: 24,
    borderRadius: 6,
    backgroundColor: colors.primaryContainer,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: typography.caption,
    fontFamily: fonts.bold,
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  loadingWrap: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearButton: {
    padding: spacing.xs,
    borderRadius: 8,
    _web: {
      transition: "background-color 0.15s ease",
      _hover: { backgroundColor: colors.hoverStrong },
    },
  },
  filterScroll: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  filterPill: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 20,
    backgroundColor: colors.surfaceVariant,
    borderWidth: 1,
    borderColor: colors.border,
    alignSelf: 'flex-start',
  },
  filterPillActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  filterPillText: {
    fontSize: typography.micro,
    fontFamily: fonts.body,
    color: colors.textSecondary,
  },
  filterPillTextActive: {
    color: colors.onPrimary,
    fontFamily: fonts.bold,
  },
  list: {
    gap: spacing.xs,
  },
}));

export default RecentlyVisitedSection;
